/**
 * @file SkuLabelPrintDialog.tsx
 * @description Печать этикеток с карточки фасовки (phase19.22, ui-design §5):
 * обёртка над складским `LabelPrintDialog` в режиме `sku`.
 *
 * Склад печати — основной склад магазина (`isDefault`), иначе первый
 * активный: калибровка листов и журнал печати живут на складе, а выбирать
 * склад в карточке товара незачем (макет §5 выбора склада не предусматривает).
 * Партии с остатком диалог загружает сам (`labelsApi.skuLots`).
 *
 * Пока склад не определился — диалог не открываем; складов нет или список не
 * загрузился — тост и закрытие.
 */

import { useEffect, useRef, useState } from "react";

import { LabelPrintDialog } from "../../../../components/wms/labels/LabelPrintDialog";
import { useNotify } from "../../../../components/ui/NotifyProvider";
import type { CatalogSku, CatalogUnit } from "../../../../lib/api-catalog";
import { ApiError } from "../../../../lib/api-client";
import { warehousesApi } from "../../../../lib/api-wms";
import { formatQtyValue } from "../../../../lib/picking-format";
import { qtyUnitLabel } from "../../../../lib/qty-input";

interface Props {
  /** Фасовка, для которой печатаем; null — диалог закрыт. */
  sku: CatalogSku | null;
  productName: string;
  unit: CatalogUnit | undefined;
  onClose: () => void;
}

export function SkuLabelPrintDialog({ sku, productName, unit, onClose }: Props) {
  const notify = useNotify();
  const [warehouseId, setWarehouseId] = useState<string | null>(null);
  // onClose родителя — стрелка, новая на каждом рендере; в зависимости эффекта
  // её не кладём, иначе загрузка складов перезапускалась бы.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Склад определяем один раз — при первом открытии.
  useEffect(() => {
    if (!sku || warehouseId) return;
    let alive = true;
    warehousesApi
      .list()
      .then((list) => {
        if (!alive) return;
        const active = list.filter((w) => w.isActive);
        const target = active.find((w) => w.isDefault) ?? active[0];
        if (target) setWarehouseId(target.id);
        else {
          notify.error("Нет активного склада — этикетки печатаются со склада.");
          onCloseRef.current();
        }
      })
      .catch((err: unknown) => {
        if (!alive) return;
        notify.error(err instanceof ApiError ? err.message : "Не удалось загрузить склады");
        onCloseRef.current();
      });
    return () => {
      alive = false;
    };
  }, [sku, warehouseId, notify]);

  if (!sku || !warehouseId) return null;

  const pack = `${formatQtyValue(sku.saleQuantity)} ${unit ? qtyUnitLabel(unit.code) : ""}`.trim();

  return (
    <LabelPrintDialog
      open
      onOpenChange={(open) => (!open ? onClose() : null)}
      warehouseId={warehouseId}
      mode="sku"
      subtitle={sku.displayName ? `${productName} — ${sku.displayName}` : productName}
      items={[
        {
          skuId: sku.id,
          skuName: sku.displayName ? `${productName} — ${sku.displayName}` : productName,
          skuCode: sku.code,
          pack,
          lotId: null,
          defaultQty: 1,
          template: sku.effectiveLabelTemplate,
        },
      ]}
    />
  );
}
